import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAuth } from '../../context/AuthContext';
import { hashPasswordClient } from '../../utils/passwordUtils';
import { useAuthPage } from '../../hooks/useAuthPage';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, CreditCard, Lock, AlertCircle } from 'lucide-react';
import { CREATE_SETUP_INTENT, SIGNUP_MUTATION } from '../../graphql/mutations';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface PricingDetails {
  name: string;
  price: number;
}

const pricingMap: Record<string, PricingDetails> = {
  basic: { name: 'Basic', price: 9 },
  pro: { name: 'Pro', price: 29 },
  enterprise: { name: 'Enterprise', price: 99 },
};

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#f2efe0',
      '::placeholder': {
        color: '#9ca3af',
      },
      backgroundColor: '#373939',
      fontFamily: 'system-ui, sans-serif',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
  hidePostalCode: true,
};

interface PaymentFormProps {
  signupData: {
    email: string;
    password: string;
    selectedPlan: string;
  };
  planDetails: PricingDetails;
  selectedPlan: string;
  onBack: () => void;
}

interface SetupIntentResponse {
  setupIntent: {
    clientSecret: string;
  };
}

interface SignupResponse {
  signup: string;
}

interface FormErrors {
  terms?: string;
  card?: string;
  submit?: string;
}

function PaymentForm({ signupData, planDetails, selectedPlan, onBack }: PaymentFormProps) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [emailInUse, setEmailInUse] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoadingSetupIntent, setIsLoadingSetupIntent] = useState<boolean>(true);

  const [createSetupIntent] = useMutation<SetupIntentResponse>(CREATE_SETUP_INTENT);
  const [signup] = useMutation<SignupResponse>(SIGNUP_MUTATION);

  const fetchSetupIntent = useCallback(async () => {
    setIsLoadingSetupIntent(true);
    try {
      const { data } = await createSetupIntent();
      if (data?.setupIntent?.clientSecret) {
        setClientSecret(data.setupIntent.clientSecret);
      }
    } catch (error) {
      console.error('Failed to create setup intent:', error);
      setErrors({ submit: 'Failed to initialize payment form. Please try again.' });
    } finally {
      setIsLoadingSetupIntent(false);
    }
  }, [createSetupIntent]);

  useEffect(() => {
    fetchSetupIntent();
  }, [fetchSetupIntent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!agreedToTerms) {
      setErrors({ terms: 'You must agree to the terms' });
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setEmailInUse(false);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement || !clientSecret) {
        setErrors({ submit: 'Payment form not ready. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: signupData.email,
          },
        },
      });

      if (stripeError) {
        setErrors({ submit: stripeError.message || 'Payment setup failed. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        const hashedPassword = await hashPasswordClient(signupData.password);
        const { data } = await signup({
          variables: {
            email: signupData.email,
            password: hashedPassword,
            accountName: signupData.email,
            paymentName: signupData.email,
            paymentMethod: setupIntent.payment_method as string,
            plan: signupData.selectedPlan,
          },
        });

        if (data?.signup) {
          login(data.signup);

          navigate('/order-confirmation', {
            state: {
              email: signupData.email,
              selectedPlan: signupData.selectedPlan,
            },
          });
        }
      }
    } catch (error) {
      console.error('Signup failed:', error);

      const rawMessage = error instanceof Error ? error.message : '';
      if (/already exists/i.test(rawMessage)) {
        setEmailInUse(true);
        setErrors({
          submit: `An account with ${signupData.email} already exists. Your card was not charged.`,
        });
      } else {
        setErrors({
          submit: rawMessage
            ? `Sign up failed: ${rawMessage}. Your card was not charged.`
            : 'Sign up failed. Please try again — your card was not charged.',
        });
      }

      // The card setup succeeded before signup failed, consuming the setup
      // intent. Fetch a fresh one so retrying doesn't hit Stripe's
      // "unexpected state" error.
      fetchSetupIntent();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingSetupIntent) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">Loading payment form...</p>
        </CardContent>
      </Card>
    );
  }

  if (!clientSecret) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-destructive">
            {errors.submit || 'Failed to initialize payment form. Please try again.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const isTestMode =
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') ||
    !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  return (
    <Card className="border-0 bg-core-dark p-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Payment Information
        </CardTitle>
        {selectedPlan === 'basic' ? (
          <CardDescription>
            Your payment will be processed after your trial period ends
          </CardDescription>
        ) : (
          <CardDescription>Your payment will be processed monthly</CardDescription>
        )}
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {/* Test Mode Banner */}
          {isTestMode && (
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-500">Test Mode</p>
                <p className="text-xs text-yellow-400/80 mt-1">
                  Use test card: 4242 4242 4242 4242, any future expiry date, and any 3-digit CVC
                </p>
                {window.location.protocol === 'http:' && (
                  <p className="text-xs text-yellow-400/80 mt-1">
                    Note: browser warnings about autofill being disabled over HTTP are expected in
                    local development and do not block payment.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="card-element">Card Details</Label>
            <div className="p-3 bg-form-dark rounded border border-cmp-line-2">
              <CardElement id="card-element" options={cardElementOptions} />
            </div>
            {errors.card && <p className="text-destructive">{errors.card}</p>}
          </div>

          <div className="flex items-start gap-3 pt-4 border-t">
            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked)}
            />
            <div className="flex-1">
              <Label htmlFor="terms" className="cursor-pointer">
                {`I agree to the terms and conditions and authorize payment of $${planDetails.price}/month after my trial period ends`}
              </Label>
              {errors.terms && <p className="text-destructive mt-1">{errors.terms}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Your payment information is encrypted and secure
            </p>
          </div>
          {errors.submit && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-destructive">{errors.submit}</p>
                {emailInUse && (
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm text-primary underline mt-1"
                  >
                    Log in to your existing account instead
                  </button>
                )}
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            className="w-full"
            disabled={!agreedToTerms || isSubmitting || !stripe}
          >
            {isSubmitting ? 'Processing...' : 'Complete Sign Up'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

interface SignupData {
  email?: string;
  password?: string;
  selectedPlan?: string;
}

export default function PaymentPage() {
  useAuthPage(); // Enable natural page scrolling
  const location = useLocation();
  const navigate = useNavigate();
  const signupData: SignupData = (location.state as SignupData) || {};

  const selectedPlan = signupData.selectedPlan || 'basic';
  const planDetails: PricingDetails = pricingMap[selectedPlan] || pricingMap.basic;

  useEffect(() => {
    if (!signupData.email || !signupData.password) {
      navigate('/signup');
    }
  }, [signupData.email, signupData.password, navigate]);

  if (!signupData.email || !signupData.password) {
    return null;
  }

  const handleBack = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid gap-6">
          {/* Order Summary */}
          <Card className="border-0 bg-core-dark p-2">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-semibold">{planDetails.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span>{signupData.email}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total (after trial)</span>
                    <span className="font-bold">{`$${planDetails.price}/month`}</span>
                  </div>
                  {selectedPlan === 'basic' ? (
                    <p className="text-muted-foreground mt-1">Free for the first 14 days</p>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Form with Stripe Elements */}
          <Elements stripe={stripePromise}>
            <PaymentForm
              signupData={{
                email: signupData.email,
                password: signupData.password,
                selectedPlan: selectedPlan,
              }}
              planDetails={planDetails}
              selectedPlan={selectedPlan}
              onBack={handleBack}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
}
